/* iter184 — Completed Jobs Missing Billing dashboard.
   Dedicated admin page that lists every completed customer job with no
   documented revenue outcome. Replaces the tiny Schedule chip drill-down as
   the primary billing-review workspace. Backed by GET /api/admin/billing-missing
   which we widened in iter184 to include billing outcome, work summary,
   completion time, and address.

   Default window: last 60 days (matches the user's "backcheck" request).
   Adjustable via a small date-range control at the top.
*/
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertOctagon, Search, ExternalLink, RefreshCw, Calendar as CalendarIcon, FileText, CheckCircle2, Wrench } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import OfficeBillingReview from './OfficeBillingReview';
import RevenueIntegrityAudit from './RevenueIntegrityAudit';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const isoDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return iso; }
};

const fmtDT = (val) => {
  if (!val) return '';
  try { return new Date(val).toLocaleString(); } catch { return String(val); }
};

const OUTCOME_BADGE = {
  office_review_required: { label: 'Office Review Required', cls: 'text-amber-200 bg-amber-500/10 border-amber-500/40' },
  create_invoice:         { label: 'Invoice Pending',        cls: 'text-blue-200 bg-blue-500/10 border-blue-500/40'   },
  save_draft:             { label: 'Draft — Office Finish',  cls: 'text-blue-200 bg-blue-500/10 border-blue-500/40'   },
  hsp_claim:              { label: 'HSP — Awaiting Invoice', cls: 'text-purple-200 bg-purple-500/10 border-purple-500/40' },
  deferred_unable_collect:{ label: 'Deferred / Unable',      cls: 'text-orange-200 bg-orange-500/10 border-orange-500/40' },
  send_invoice:           { label: 'Send Invoice',           cls: 'text-blue-200 bg-blue-500/10 border-blue-500/40'   },
  membership_included:    { label: 'Membership — $0',        cls: 'text-green-300 bg-green-500/10 border-green-500/40' },
};

const outcomeChip = (key) => {
  if (!key) {
    return { label: 'No Outcome (blank)', cls: 'text-red-300 bg-red-500/10 border-red-500/50' };
  }
  return OUTCOME_BADGE[key] || { label: key.replace(/_/g, ' '), cls: 'text-[#A3A3A3] bg-[#1a1a1a] border-[#3a3a3a]' };
};

const BillingMissing = () => {
  const [startDate, setStartDate] = useState(isoDaysAgo(60));
  const [endDate, setEndDate] = useState(isoDaysAgo(-1)); // through tomorrow (covers today)
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [tab, setTab] = useState('missing');
  const [orCounts, setORCounts] = useState({});
  const [repairRow, setRepairRow] = useState(null);

  // Load office-review counts for the tab badges (independent of the active tab).
  const loadCounts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/office-billing-review?status=office_review_required`, { withCredentials: true });
      if (data?.counts) setORCounts(data.counts);
    } catch { /* non-fatal */ }
  }, []);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('start_date', startDate);
      params.set('end_date', endDate);
      const { data } = await axios.get(`${API}/admin/billing-missing?${params}`, { withCredentials: true });
      setItems(data?.items || []);
      setCount(data?.count || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load billing-missing list');
      setItems([]); setCount(0);
    }
    setLoading(false);
  }, [startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((it) => {
      const haystack = [
        it.customer_name, it.address, it.employee_name,
        it.shift_title, it.service_type, it.work_performed,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(needle);
    });
  }, [items, q]);

  const setPreset = (days) => {
    setStartDate(isoDaysAgo(days));
    setEndDate(isoDaysAgo(-1));
  };

  return (
    <div className="p-6 space-y-6" data-testid="billing-missing-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <AlertOctagon className="w-6 h-6 text-amber-400" />
            <h1 className="text-2xl font-bold text-white">Completed Jobs Missing Billing</h1>
          </div>
          <p className="text-sm text-[#A3A3A3] mt-1 max-w-2xl">
            Every completed customer job that has no invoice, no HSP/warranty draft,
            no no-charge decision, or is parked in <span className="text-amber-300">Office Billing Review Required</span> shows up here.
            Resolve each one by creating an invoice or marking a decision.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button data-testid="preset-14d" size="sm" variant="outline" onClick={() => setPreset(14)}>Last 14d</Button>
          <Button data-testid="preset-30d" size="sm" variant="outline" onClick={() => setPreset(30)}>Last 30d</Button>
          <Button data-testid="preset-60d" size="sm" variant="outline" onClick={() => setPreset(60)}>Last 60d (Backcheck)</Button>
          <Button data-testid="refresh" size="sm" onClick={load} disabled={loading} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* iter205 — Billing workspace tabs */}
      <div className="flex items-center gap-1 border-b border-[#262626]" data-testid="billing-tabs">
        {[
          ['missing', 'Billing Missing', count],
          ['office_review_required', 'Office Billing Review', orCounts.office_review_required || 0],
          ['returned_to_tech', 'Returned to Technician', orCounts.returned_to_tech || 0],
          ['finalized', 'Resolved / Finalized', orCounts.finalized || 0],
          ['revenue_audit', 'Revenue Audit', 0],
        ].map(([k, label, n]) => (
          <button key={k} type="button" data-testid={`tab-${k}`} onClick={() => setTab(k)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? 'border-[#D4AF37] text-white' : 'border-transparent text-[#A3A3A3] hover:text-white'}`}>
            {label}
            {n > 0 && <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full ${k === 'office_review_required' ? 'bg-amber-500/20 text-amber-300' : 'bg-[#262626] text-[#D4D4D4]'}`}>{n}</span>}
          </button>
        ))}
      </div>

      {tab === 'revenue_audit' && (
        <RevenueIntegrityAudit />
      )}

      {tab !== 'missing' && tab !== 'revenue_audit' && (
        <OfficeBillingReview status={tab} onCountsChange={setORCounts} />
      )}

      {tab === 'missing' && (<>
      {/* Filters */}
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">Start</label>
          <Input data-testid="start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-40" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">End</label>
          <Input data-testid="end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-40" />
        </div>
        <div className="flex-1 min-w-[240px]">
          <label className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">Search</label>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#525252]" />
            <Input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Customer, address, tech, work performed..."
              className="pl-8"
            />
          </div>
        </div>
        <div className="text-sm text-[#A3A3A3] pb-2">
          <span className="text-white font-semibold" data-testid="count">{filtered.length}</span> of {count}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center text-[#A3A3A3] py-16">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-[#262626] rounded-lg bg-[#0d0d0d]" data-testid="empty-state">
          <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-3" />
          <div className="text-white font-semibold">All jobs are billed and reviewed 🎉</div>
          <div className="text-xs text-[#A3A3A3] mt-1">No completed jobs missing billing in this window.</div>
        </div>
      ) : (
        <div className="border border-[#262626] rounded-lg overflow-hidden" data-testid="results-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#141414] text-[10px] uppercase tracking-wider text-[#A3A3A3]">
                <tr>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Customer</th>
                  <th className="text-left px-3 py-2">Address</th>
                  <th className="text-left px-3 py-2">Tech</th>
                  <th className="text-left px-3 py-2">Job / Work</th>
                  <th className="text-right px-3 py-2">Amount</th>
                  <th className="text-left px-3 py-2">Completed</th>
                  <th className="text-left px-3 py-2">Billing Status</th>
                  <th className="text-right px-3 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => {
                  const chip = outcomeChip(it.invoice_status);
                  return (
                    <tr key={it.id} className="border-t border-[#262626] hover:bg-[#141414]" data-testid={`row-${it.id}`}>
                      <td className="px-3 py-2 text-white whitespace-nowrap">
                        <CalendarIcon className="inline w-3 h-3 mr-1 text-[#525252]" />
                        {fmtDate(it.date)}
                        <div className="text-[10px] text-[#A3A3A3]">{it.start_time || ''}</div>
                      </td>
                      <td className="px-3 py-2 text-white whitespace-nowrap">{it.customer_name || '—'}</td>
                      <td className="px-3 py-2 text-[#D4D4D4] max-w-[240px] truncate" title={it.address || ''}>
                        {it.address || '—'}
                      </td>
                      <td className="px-3 py-2 text-[#D4D4D4] whitespace-nowrap">{it.employee_name || '—'}</td>
                      <td className="px-3 py-2 text-[#D4D4D4] max-w-[280px]">
                        <div className="truncate" title={it.shift_title}>{it.shift_title || '—'}</div>
                        {it.work_performed && (
                          <div className="text-[10px] text-[#A3A3A3] truncate" title={it.work_performed}>
                            {it.work_performed}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-white whitespace-nowrap">
                        {it.estimated_amount != null && it.estimated_amount !== ''
                          ? `$${Number(it.estimated_amount).toFixed(2)}`
                          : <span className="text-[#525252]">—</span>}
                      </td>
                      <td className="px-3 py-2 text-[#D4D4D4] text-[11px] whitespace-nowrap">
                        {fmtDT(it.completion_submitted_at) || fmtDT(it.actual_end_time)}
                      </td>
                      <td className="px-3 py-2">
                        <Badge className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border ${chip.cls}`}>
                          {chip.label}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {it.invoice_status === 'office_review_required' && it.completion_id && (
                          <Button size="sm" variant="outline" data-testid={`repair-${it.id}`} onClick={() => setRepairRow(it)}
                            className="mr-2 text-[11px] border-amber-500/40 text-amber-300">
                            <Wrench className="w-3 h-3 mr-1" /> Create Office Review Draft
                          </Button>
                        )}
                        <Link
                          to={`/admin/schedule?open=${it.id}`}
                          data-testid={`open-${it.id}`}
                          className="inline-flex items-center gap-1 text-[#D4AF37] hover:text-[#F3E5AB] text-xs font-semibold"
                        >
                          <FileText className="w-3 h-3" /> Open Job <ExternalLink className="w-3 h-3" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </>)}

      <RepairModal row={repairRow} onClose={() => setRepairRow(null)} onDone={() => { setRepairRow(null); load(); loadCounts(); }} />
    </div>
  );
};

const REPAIR_BASES = [
  ['final_known', 'Final amount known'],
  ['previously_quoted', 'Previously quoted amount'],
  ['office_to_determine', 'Office to determine amount'],
];

const RepairModal = ({ row, onClose, onDone }) => {
  const [basis, setBasis] = useState('office_to_determine');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (row) { setBasis('office_to_determine'); setAmount(''); setReason(''); } }, [row]);
  if (!row) return null;

  const submit = async () => {
    if (!reason.trim()) { toast.error('A reason is required.'); return; }
    if ((basis === 'final_known' || basis === 'previously_quoted') && (amount === '' || Number(amount) <= 0)) {
      toast.error('Enter the invoice amount.'); return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/completions/${row.completion_id}/create-office-review-draft`, {
        amount_basis: basis,
        proposed_amount: amount === '' ? null : Number(amount),
        reason: reason.trim(),
      }, { withCredentials: true });
      toast.success('Office review draft created');
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create draft');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!row} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#141414] border border-[#262626] text-white" data-testid="repair-modal">
        <DialogHeader>
          <DialogTitle className="text-white">Create Office Review Draft</DialogTitle>
          <DialogDescription className="text-[#A3A3A3]">
            {row.customer_name} · {row.date} — this completion was left in Office Review with no invoice. Create the missing draft (the completion and technician are preserved; nothing is duplicated).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#A3A3A3]">Amount basis</label>
            <div className="grid gap-2 mt-1">
              {REPAIR_BASES.map(([k, label]) => (
                <button key={k} type="button" data-testid={`repair-basis-${k}`} onClick={() => setBasis(k)}
                  className={`text-left rounded-md border px-3 py-2 text-sm ${basis === k ? 'border-amber-400 bg-amber-500/10 text-white' : 'border-[#262626] text-[#D4D4D4]'}`}>{label}</button>
              ))}
            </div>
          </div>
          {(basis === 'final_known' || basis === 'previously_quoted') && (
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#A3A3A3]">Invoice amount ($)</label>
              <Input type="number" inputMode="decimal" data-testid="repair-amount" value={amount} onChange={(e) => setAmount(e.target.value)}
                className="bg-[#0A0A0A] border-[#262626] text-white mt-1 w-40" placeholder="0.00" />
            </div>
          )}
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#A3A3A3]">Reason (required)</label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="repair-reason"
              className="bg-[#0A0A0A] border-[#262626] text-white mt-1" rows={2} placeholder="Why this needs an office review draft" />
          </div>
        </div>
        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose} className="border-[#262626] text-[#A3A3A3]" data-testid="repair-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="repair-confirm" className="bg-amber-500 text-black hover:bg-amber-400">Create Draft</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BillingMissing;
